/**
 * Shipped building blocks for the centred prompt card.
 *
 * Same tokens the sidebar prompt already used (telekinesis-*, gold #FFBA28,
 * Material-Symbols icons, `cn`) — this is a re-shape of the existing prompt,
 * not a new design system. The DEV mockup board (/prompt-card-harness) renders
 * from these same parts so the harness can never drift from what ships.
 */
import type { ReactNode } from 'react';
import { cn } from '@/lib/utils';
import { ChevronRight, ImagePlus, Zap } from '@/components/ui/icons';
import { CHECKER, MODE_LABELS, type PromptMode } from './cardTokens';

/** Segmented mode switch — gold-outline active pill on a dark track. */
export function ModeTabs({
  modes,
  active,
  onChange,
}: {
  modes: PromptMode[];
  active: PromptMode;
  onChange: (m: PromptMode) => void;
}) {
  return (
    <div
      className="flex w-full rounded-full bg-telekinesis-neutral-900/80 p-1.5"
      role="tablist"
    >
      {modes.map((m) => (
        <button
          key={m}
          role="tab"
          aria-selected={active === m}
          onClick={() => onChange(m)}
          className={cn(
            'flex-1 rounded-full px-5 py-2.5 text-base font-semibold transition-all duration-200',
            active === m
              ? 'border-2 border-telekinesis-gold bg-telekinesis-gold/10 text-telekinesis-gold'
              : 'text-telekinesis-text-secondary hover:text-telekinesis-text-primary',
          )}
        >
          {MODE_LABELS[m]}
        </button>
      ))}
    </div>
  );
}

/**
 * Dashed upload well. Click and drop both route to `onFiles`; the caller owns
 * what happens to the files (image attach vs. mesh reconstruction).
 */
export function UploadWell({
  accept,
  hint,
  label = 'Upload an image',
  onFiles,
  disabled,
}: {
  accept: string;
  hint: string;
  label?: string;
  onFiles: (files: FileList) => void;
  disabled?: boolean;
}) {
  const pick = () => {
    if (disabled) return;
    const input = document.createElement('input');
    input.type = 'file';
    input.accept = accept;
    input.onchange = (e) => {
      const files = (e.target as HTMLInputElement).files;
      if (files?.length) onFiles(files);
    };
    input.click();
  };
  return (
    <button
      type="button"
      onClick={pick}
      disabled={disabled}
      onDragOver={(e) => e.preventDefault()}
      onDrop={(e) => {
        e.preventDefault();
        if (!disabled && e.dataTransfer.files?.length) {
          onFiles(e.dataTransfer.files);
        }
      }}
      className={cn(
        'flex h-[240px] w-full flex-col items-center justify-center gap-2 rounded-2xl',
        'border-2 border-dashed border-telekinesis-neutral-700 bg-telekinesis-neutral-950/60',
        'transition-colors duration-200 hover:border-telekinesis-gold/70',
        disabled && 'cursor-not-allowed opacity-50',
      )}
      style={CHECKER}
    >
      <ImagePlus className="h-9 w-9 text-telekinesis-text-secondary" />
      <span className="text-base font-medium text-telekinesis-text-primary">
        {label}
      </span>
      <span className="text-sm text-telekinesis-text-tertiary">{hint}</span>
    </button>
  );
}

/** "Try ▸ [samples]" starter row. */
export function TryRow({
  samples,
  onPick,
  right,
}: {
  samples: { src: string; prompt: string }[];
  onPick: (prompt: string) => void;
  right?: ReactNode;
}) {
  return (
    <div className="flex items-center justify-between gap-3">
      <div className="flex items-center gap-2">
        <span className="text-sm text-telekinesis-text-secondary">Try</span>
        {samples.map((s) => (
          <button
            key={s.src}
            onClick={() => onPick(s.prompt)}
            title={s.prompt}
            className="h-12 w-12 overflow-hidden rounded-xl border border-telekinesis-neutral-700 bg-telekinesis-neutral-900 transition-all duration-200 hover:scale-105 hover:border-telekinesis-gold"
          >
            <img src={s.src} alt="" className="h-full w-full object-cover" />
          </button>
        ))}
        <ChevronRight className="h-5 w-5 text-telekinesis-text-tertiary" />
      </div>
      {right}
    </div>
  );
}

/**
 * Full-width gold CTA with the credit cost. `loading` swaps it to a stop
 * button, mirroring the sidebar Generate button it replaces.
 */
export function GenerateBar({
  label = 'Generate Model',
  credits,
  disabled,
  loading,
  onClick,
  onStop,
}: {
  label?: string;
  credits?: number;
  disabled?: boolean;
  loading?: boolean;
  onClick: () => void;
  onStop?: () => void;
}) {
  if (loading) {
    return (
      <button
        onClick={onStop}
        className="flex w-full items-center justify-center gap-3 rounded-full bg-[#FFBA28] py-4 text-xl font-bold text-black transition-all duration-300 hover:shadow-[0_0_28px_rgba(255,186,40,0.55)]"
      >
        <span className="h-4 w-4 rounded-[2px] bg-black" />
        Stop Generation
      </button>
    );
  }
  return (
    <button
      onClick={onClick}
      disabled={disabled}
      className={cn(
        'flex w-full items-center justify-center gap-3 rounded-full bg-[#FFBA28] py-4 text-xl font-bold text-black',
        'transition-all duration-300 hover:shadow-[0_0_28px_rgba(255,186,40,0.55)]',
        'disabled:opacity-50 disabled:hover:shadow-none',
      )}
    >
      {label}
      {credits != null && (
        <span className="flex items-center gap-1.5 rounded-full bg-black/85 px-2.5 py-1 text-white">
          <Zap className="h-4 w-4" />
          <span className="text-base">{credits}</span>
        </span>
      )}
    </button>
  );
}
